import { Link } from 'react-router-dom';
import type { BlogPost } from '@/types/blog';

interface BlogCardProps {
  post: BlogPost;
  locale: string;
  readMoreLabel: string;
  onHover?: () => void;
}

export default function BlogCard({ post, locale, readMoreLabel, onHover }: BlogCardProps) {
  const isZh = locale === 'zh-TW';
  const title = isZh && post.title_zh ? post.title_zh : post.title;
  const summary = isZh && post.summary_zh ? post.summary_zh : post.summary;

  return (
    <Link to={`/blog/${post.slug}`} onMouseEnter={onHover}>
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden hover:border-[var(--cyan-electric)]/50 hover:scale-[1.02] transition-all duration-300 h-full flex flex-col group">
        {/* Cover Image */}
        <div className="h-48 overflow-hidden relative">
          {post.cover_image ? (
            <img
              src={post.cover_image}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[var(--navy-light)] to-[var(--navy-mid)] flex items-center justify-center">
              <svg
                className="w-16 h-16 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                />
              </svg>
            </div>
          )}
          {/* Category badge */}
          <span className="absolute top-3 left-3 px-2 py-1 bg-[var(--amber-warm)]/90 text-black text-xs font-medium rounded uppercase tracking-wider">
            {post.category}
          </span>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="text-xl font-bold text-white group-hover:text-[var(--cyan-electric)] transition-colors line-clamp-2">
            {title}
          </h3>
          <p className="text-gray-400 mt-3 line-clamp-3 flex-1">{summary}</p>

          {/* Footer */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {post.published_at
                ? new Date(post.published_at).toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </span>
            <span className="text-[var(--cyan-electric)] text-sm font-medium group-hover:translate-x-1 transition-transform">
              {readMoreLabel} &rarr;
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
